import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Checkbox,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
  Box,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/system';
import { useDispatch } from 'react-redux';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';

// Components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import { UserMoreMenu } from '../../components/_dashboard/user';
import { fDateNew, fDateTime2 } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
import RevenueToolbar from './RevenueToolbar';
import {
  getMisMatchedDownloadList,
  getRevenueGridList
} from '../../store/Slice/Revenue/RevenueSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import {
  getBankDropdownData,
  getLoanTypeData,
  getMasterListType
} from '../../store/Slice/Global/GlobalSlice';
import { isValidValue, normalizeValue, transformToSelectOptions } from '../../utils/CommonFunction';
import useRevenueSelector from '../../store/Slice/Revenue/RevenueSelector';
import useDebounce from '../../utils/useDebounce';

const StyledHeadCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    backgroundColor: '#ffffff',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px'
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap'
  }
}));

export default function RevenueList() {
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('name');
  const [selected, setSelected] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [downloadPopup, setDownloadPopup] = useState(false);
  const [filterDatesRange, setFilterDatesRange] = useState([
    { startDate: null, endDate: null, key: 'selection' }
  ]);
  const [filterParameter, setFilterParameter] = useState({
    loanTypeId: null,
    bankId: null,
    branchId: null,
    misIsMatch: null,
    misIsMatchId: null
  });
  const [fileTypeId, setFileTypeId] = useState(null);
  const [currentTabLabel, setCurrentTabLabel] = useState('Disbursed');
  const [masterTypeOptions, setMasterTypeOptions] = useState({
    fileStatusOptions: [],
    statusOptions: []
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const debouncedSearch = useDebounce(filterName, 600);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { RevenueListGridData } = useRevenueSelector();

  const handleDownloadMISDetails = async () => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      fileFormat: 'csv'
    };

    const res = await dispatch(getMisMatchedDownloadList(credentials));
    if (!res || !res.success || !res.data) {
      console.error('❌ No CSV data found!');
      return;
    }

    const csvText = String(res.data);
    try {
      const blob = new Blob([`\uFEFF${csvText}`], {
        type: 'text/csv;charset=utf-8;'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RevenueMisMatchReport.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloadPopup(false);
    }
  };

  // === Fetch Revenue List ===
  const getRevenueListApi = useCallback(() => {
    if (!fileTypeId) return;

    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        sortOrder: order,
        pageNumber: page + 1
      },
      filterModel: {
        bankId: filterParameter?.bankId || null,
        branchId: filterParameter?.branchId || null,
        loanTypeId: filterParameter?.loanTypeId || null,
        misIsMatch: isValidValue(filterParameter?.misIsMatch)
          ? filterParameter?.misIsMatch === 'Matched'
          : null,
        fromDate:
          filterDatesRange[0].startDate !== null ? fDateTime2(filterDatesRange[0].startDate) : null,
        toDate:
          filterDatesRange[0].endDate !== null ? fDateTime2(filterDatesRange[0].endDate) : null
      },
      revenueFileStatusId: fileTypeId
    };

    dispatch(getRevenueGridList(credentials));
  }, [
    dispatch,
    LoginData,
    debouncedSearch,
    order,
    filterDatesRange,
    fileTypeId,
    filterParameter,
    page,
    rowsPerPage
  ]);

  useEffect(() => {
    getRevenueListApi();
  }, [getRevenueListApi]);

  // === Master Data (FileType, Status, Bank, Loan) ===
  const MasterTypeApi = async () => {
    const [statusType, fileType] = ['REVENUE_STATUS_TYPE', 'REVENUE_FILE_STATUS_TYPE'];
    const [statusTypeRes, fileRes] = await Promise.all([
      dispatch(getMasterListType(statusType)),
      dispatch(getMasterListType(fileType))
    ]);

    const statusOptions = transformToSelectOptions(
      statusTypeRes?.data?.masterTypes || [],
      'typeName',
      'typeId'
    );
    const fileStatusOptions = transformToSelectOptions(
      fileRes?.data?.masterTypes || [],
      'typeName',
      'typeId'
    );

    const disbursedOption = fileStatusOptions.find((o) =>
      o?.label?.toLowerCase().includes('disbursed')
    );
    setFileTypeId(disbursedOption?.value || null);
    if (disbursedOption?.label) {
      setCurrentTabLabel(disbursedOption.label);
    }
    setMasterTypeOptions({ fileStatusOptions, statusOptions });
  };

  const getLoanMasterApi = useCallback(() => {
    const obj = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getLoanTypeData(obj));
  }, [dispatch, LoginData]);

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId, isWithCode: true };
    dispatch(getBankDropdownData(data));
  }, [dispatch, LoginData]);

  useEffect(() => {
    MasterTypeApi();
    getBankDropApi();
    getLoanMasterApi();
  }, []);

  // === Common Handlers ===
  const handleRequestSort = (property) => {
    if (property !== 'action') {
      setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(RevenueListGridData || []);
      return;
    }
    setSelected([]);
  };

  const handleFilterByName = (e) => setFilterName(e.target.value);

  const handleClick = (event, item) => {
    const selectedIndex = selected.indexOf(item);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, item);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleTabChange = (newValue, label) => {
    setFileTypeId(newValue);
    if (label) setCurrentTabLabel(label);
    setFilterDatesRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setFilterParameter({ loanTypeId: null, bankId: null, branchId: null, misIsMatch: null });
    setPage(0);
  };

  // === Generate Columns Dynamically per File Type ===
  const getTableColumns = (typeName) => {
    const isApproved = typeName?.toLowerCase().includes('approved');
    const isDisbursed = typeName?.toLowerCase().includes('disbursed');

    const dateColumn = isApproved
      ? { id: 'fileApprovalDate', label: 'APPROVAL DATE', sortable: true }
      : isDisbursed
      ? { id: 'fileDisbursedDate', label: 'DISBURSED DATE', sortable: true }
      : { id: 'fileRequestDate', label: 'REQUEST DATE', sortable: true };

    const amountColumn = isDisbursed
      ? { id: 'loanAmountDisbursed', label: 'DISBURSED AMOUNT', sortable: true }
      : { id: 'loanAmountRequested', label: 'LOAN AMOUNT', sortable: true };

    return [
      { id: 'bankName', label: 'BANK NAME', sortable: true },
      { id: 'fileNumber', label: 'FILE NO', sortable: true },
      { id: 'branchName', label: 'BRANCH NAME', sortable: true },
      dateColumn,
      { id: 'borrowerName', label: 'BORROWER NAME', sortable: true },
      { id: 'empClass', label: 'EMP CLASS', sortable: true },
      { id: 'loanTypeName', label: 'LOAN PRODUCT TYPE', sortable: true },
      amountColumn,
      { id: 'misMonth', label: 'MIS MONTH', sortable: true },
      { id: 'misStatus', label: 'MIS STATUS', sortable: true },
      { id: 'action', label: 'ACTION', sortable: false }
    ];
  };

  useEffect(() => {
    if (!RevenueListGridData) {
      setHasNextPage(true);
      return;
    }
    setHasNextPage(RevenueListGridData.length >= rowsPerPage);
  }, [RevenueListGridData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    setSelected([]);
  }, [RevenueListGridData]);

  const totalRecords = Array.isArray(RevenueListGridData) ? RevenueListGridData.length : 0;
  const currentColumns = getTableColumns(currentTabLabel);

  return (
    <Page title="Partner Management System || Revenue">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title, and Breadcrumbs */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          {/* Left Title with Icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <AccountBalanceOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Bank MIS Detail
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                View and manage bank-wise MIS details for your processed loans.
              </Typography>
            </Box>
          </Stack>

          {/* Right Breadcrumbs */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box>
              <BrudCrumbs />
            </Box>
            <Tooltip title="Download Mismatch Report" placement="top" arrow>
              <IconButton
                onClick={() => setDownloadPopup(true)}
                sx={{
                  width: 40,
                  height: 40,
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  color: '#0284c7',
                  '&:hover': {
                    borderColor: '#0284c7',
                    bgcolor: 'rgba(2, 132, 199, 0.06)'
                  }
                }}
              >
                <CloudDownloadOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Main Card Container */}
        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          {/* Toolbar with 3 Tab Pills on Left and Action Tools on Right */}
          <RevenueToolbar
            parent="revenue"
            activeTab={currentTabLabel}
            onTabChange={handleTabChange}
            fileStatusOptions={masterTypeOptions.fileStatusOptions}
            selected={selected}
            filterName={filterName}
            onFilterName={handleFilterByName}
            filterDatesRange={filterDatesRange}
            setFilterDatesRange={setFilterDatesRange}
            setFilterParameter={setFilterParameter}
            filterParameter={filterParameter}
            masterTypeOptions={masterTypeOptions}
          />

          {/* Table Container */}
          <Scrollbar>
            <TableContainer sx={{ minWidth: 960 }}>
              <Table>
                <thead>
                  <TableRow>
                    <StyledHeadCell padding="checkbox" sx={{ pl: 2.5 }}>
                      <Checkbox
                        indeterminate={
                          selected.length > 0 &&
                          selected.length < (RevenueListGridData?.length || 0)
                        }
                        checked={
                          RevenueListGridData?.length > 0 &&
                          selected.length === RevenueListGridData?.length
                        }
                        onChange={handleSelectAllClick}
                        sx={{
                          color: '#cbd5e1',
                          '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                        }}
                      />
                    </StyledHeadCell>
                    {currentColumns.map((headCell) => (
                      <StyledHeadCell
                        key={headCell.id}
                        onClick={() => headCell.sortable && handleRequestSort(headCell.id)}
                        sx={{
                          cursor: headCell.sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                          ...(headCell.id === 'action' ? { textAlign: 'right', pr: 3 } : {})
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          justifyContent={headCell.id === 'action' ? 'flex-end' : 'flex-start'}
                        >
                          <span>{headCell.label}</span>
                          {headCell.sortable && (
                            <SwapVertIcon
                              sx={{
                                fontSize: 16,
                                color: orderBy === headCell.id ? '#0284c7' : '#94a3b8'
                              }}
                            />
                          )}
                        </Stack>
                      </StyledHeadCell>
                    ))}
                  </TableRow>
                </thead>
                <TableBody>
                  {RevenueListGridData && RevenueListGridData.length > 0 ? (
                    RevenueListGridData.map((item, index) => {
                      const isItemSelected = selected.indexOf(item) !== -1;
                      const misStatus = item?.misStatus || item?.status;

                      return (
                        <TableRow
                          hover
                          key={item.revenueId || index}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          sx={{
                            '&:hover': { backgroundColor: 'rgba(2, 132, 199, 0.03)' },
                            '&.Mui-selected, &.Mui-selected:hover': {
                              backgroundColor: 'rgba(2, 132, 199, 0.06)'
                            }
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ pl: 2.5 }}>
                            <Checkbox
                              checked={isItemSelected}
                              onChange={(event) => handleClick(event, item)}
                              sx={{
                                color: '#cbd5e1',
                                '&.Mui-checked': { color: '#0284c7' }
                              }}
                            />
                          </TableCell>

                          {currentColumns.map((col) => {
                            if (col.id === 'action') {
                              return (
                                <StyledTableCell key={col.id} align="right" sx={{ pr: 2 }}>
                                  <UserMoreMenu
                                    revenueId={item?.revenueId}
                                    leadId={item?.leadId}
                                    applicationId={item?.applicationId}
                                    rowId={item}
                                    parent={`revenue-${currentTabLabel.toLowerCase()}`}
                                  />
                                </StyledTableCell>
                              );
                            }

                            if (col.id === 'fileNumber') {
                              return (
                                <StyledTableCell key={col.id}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      color: '#0284c7',
                                      fontSize: '13px'
                                    }}
                                  >
                                    {normalizeValue(item[col.id], NOT_AVAILABLE)}
                                  </Typography>
                                </StyledTableCell>
                              );
                            }

                            if (col.id === 'misStatus') {
                              const isMatched =
                                misStatus?.toLowerCase().includes('match') &&
                                !misStatus?.toLowerCase().includes('mismatch');

                              return (
                                <StyledTableCell key={col.id} sx={{ whiteSpace: 'nowrap' }}>
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      whiteSpace: 'nowrap',
                                      px: 1.5,
                                      py: 0.35,
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      bgcolor: isMatched ? '#dcfce7' : '#fef3c7',
                                      color: isMatched ? '#16a34a' : '#d97706'
                                    }}
                                  >
                                    {normalizeValue(item[col.id], NOT_AVAILABLE)}
                                  </Box>
                                </StyledTableCell>
                              );
                            }

                            if (col.id.includes('Date')) {
                              return (
                                <StyledTableCell key={col.id} sx={{ color: '#475569' }}>
                                  {item[col.id] ? fDateNew(item[col.id]) : NOT_AVAILABLE}
                                </StyledTableCell>
                              );
                            }

                            if (col.id.includes('Amount')) {
                              return (
                                <StyledTableCell
                                  key={col.id}
                                  sx={{ fontWeight: 600, color: '#0f172a' }}
                                >
                                  {item[col.id]
                                    ? `₹ ${Number(item[col.id]).toLocaleString('en-IN')}`
                                    : NOT_AVAILABLE}
                                </StyledTableCell>
                              );
                            }

                            return (
                              <StyledTableCell key={col.id} sx={{ color: '#334155' }}>
                                {item[col.id] || NOT_AVAILABLE}
                              </StyledTableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell align="center" colSpan={currentColumns.length + 1} sx={{ py: 8 }}>
                        {/* Beautiful Empty State matching Screenshot */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 2
                          }}
                        >
                          {/* Circular Glow with Search Document Graphic */}
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: '50%',
                              bgcolor: '#eff6ff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 2.5
                            }}
                          >
                            <svg
                              width="42"
                              height="42"
                              viewBox="0 0 42 42"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect
                                x="7"
                                y="5"
                                width="22"
                                height="28"
                                rx="3"
                                fill="#ffffff"
                                stroke="#93c5fd"
                                strokeWidth="2"
                              />
                              <path
                                d="M12 12H21"
                                stroke="#60a5fa"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                              <path
                                d="M12 17H24"
                                stroke="#bfdbfe"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                              <path
                                d="M12 22H20"
                                stroke="#bfdbfe"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                              <circle
                                cx="28"
                                cy="27"
                                r="6.5"
                                fill="#ffffff"
                                stroke="#0284c7"
                                strokeWidth="2.5"
                              />
                              <path
                                d="M33 32L37 36"
                                stroke="#0284c7"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </Box>

                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: '18px',
                              color: '#0f172a',
                              mb: 0.75
                            }}
                          >
                            No records found
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: '13.5px',
                              color: '#64748b',
                              mb: 2.5,
                              maxWidth: 380,
                              textAlign: 'center'
                            }}
                          >
                            No results found for your search. Try different keywords or clear
                            filters.
                          </Typography>

                          <Button
                            variant="outlined"
                            onClick={() => {
                              setFilterName('');
                              setFilterParameter({
                                loanTypeId: null,
                                bankId: null,
                                branchId: null,
                                misIsMatch: null,
                                misIsMatchId: null
                              });
                            }}
                            startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
                            sx={{
                              borderRadius: '8px',
                              borderColor: '#0284c7',
                              color: '#0284c7',
                              px: 2.5,
                              py: 0.75,
                              fontSize: '13px',
                              fontWeight: 600,
                              textTransform: 'none',
                              '&:hover': {
                                borderColor: '#0369a1',
                                bgcolor: 'rgba(2, 132, 199, 0.04)'
                              }
                            }}
                          >
                            Search Again
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Pagination Footer matching screenshot */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            {/* Left: Range text */}
            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
              Showing {totalRecords > 0 ? page * rowsPerPage + 1 : 0} to{' '}
              {totalRecords > 0 ? page * rowsPerPage + totalRecords : 0} records
            </Typography>

            {/* Right: Rows per page + Page Buttons */}
            <Stack direction="row" alignItems="center" spacing={2.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Rows per page</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    fontSize: '13px',
                    height: 32,
                    borderRadius: '8px',
                    bgcolor: '#fff',
                    '& fieldset': { borderColor: '#e2e8f0' }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Stack>

              {/* Page Navigation Buttons */}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.5,
                    color: '#64748b',
                    '&:disabled': { opacity: 0.4 }
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>

                <Button
                  size="small"
                  sx={{
                    minWidth: 32,
                    height: 32,
                    p: 0,
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 700,
                    bgcolor: '#0284c7 !important',
                    color: '#ffffff !important'
                  }}
                >
                  {page + 1}
                </Button>

                <IconButton
                  size="small"
                  disabled={!hasNextPage}
                  onClick={() => setPage((prev) => prev + 1)}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.5,
                    color: '#64748b',
                    '&:disabled': { opacity: 0.4 }
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </Card>

        <Dialog
          open={downloadPopup}
          onClose={() => setDownloadPopup(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2.5,
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '16px' }}>
              Download
            </Typography>
            <IconButton
              onClick={() => setDownloadPopup(false)}
              size="small"
              sx={{ color: '#64748b' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography
              variant="body2"
              sx={{ color: '#475569', fontSize: '14px', lineHeight: 1.5 }}
            >
              Are you sure you want to download MisMatch Details Report?
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}
          >
            <Button
              variant="contained"
              onClick={handleDownloadMISDetails}
              sx={{
                borderRadius: '8px',
                bgcolor: '#0284c7',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                boxShadow: '0 2px 8px rgba(2, 132, 199, 0.25)',
                '&:hover': { bgcolor: '#0369a1' }
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
